import { Dictionary } from 'vuex';

import { MenuItem } from '@/interfaces/menuItem';
import store from '@/store/store';
import { flat } from './flat';

/**
 * Znajduje podstawowe elementy do budowania menu
 *
 * @export
 * @param {*} routes
 * @returns {(any[] | undefined)}
 */
export function getMenuElements(routes: any[]): any[] | undefined {
  const rootRoute = routes.find(route => route.meta && route.meta.menuList && route.children);
  if (rootRoute) {
    return rootRoute.children;
  }
}

/**
 * Buduje menu nawigacji
 *
 * @export
 * @param {any[]} routes
 * @param {string} [query]
 * @returns {Promise<any[]>}
 */
export async function buildMenu(routes: any[], query?: string): Promise<any[]> {
  const menuRoutes = routes.filter(route => route.meta && route.meta.menu);
  const allMenuItems: MenuItem[] = await Promise.all(menuRoutes.map(route => routeToMenuItem(route, query)));
  return filterUnpermittedMenuItems(filterQueriedMenuItems(allMenuItems));
}

/**
 * Mapuje element routera na element menu
 *
 * @param {*} route
 * @param {string} [query]
 * @returns {Promise<MenuItem>}
 */
async function routeToMenuItem(route: any, query?: string): Promise<MenuItem> {
  const menuItem = { name: route.name, ...route.meta.menu };
  if (route.children && route.children.length) {
    const children = await buildMenu(route.children, query);
    if (children.length) {
      menuItem.children = children;
    }
  }
  return menuItem;
}

/**
 * Odfiltrowuje elementy niepasujące do wyszukania
 *
 * @param {MenuItem[]} menuItems
 * @param {string} [query]
 * @returns {MenuItem[]}
 */
function filterQueriedMenuItems(menuItems: MenuItem[], query?: string): MenuItem[] {
  return menuItems.filter(
    menuItem =>
      !query ||
      (menuItem.children && menuItem.children.length) ||
      menuItem.title.toLowerCase().includes(query.toLowerCase())
  );
}

/**
 * Odfiltrowuje elementy menu, do których użytkownik nie ma dostępu
 *
 * @param {any[]} menuItems
 * @returns {Promise<any[]>}
 */
async function filterUnpermittedMenuItems(menuItems: MenuItem[]): Promise<MenuItem[]> {
  const requiredPermissions = getRequiredPermissions(menuItems);
  const permissionsMap: Dictionary<boolean> = await store.dispatch('auth/checkPermissions', requiredPermissions);

  return menuItems.filter(menuItem =>
    menuItemToPermissions(menuItem).every((permission: string) => permissionsMap[permission])
  );
}

/**
 * Wyciąga listę wszystkich możliwych wymaganych uprawnień do wyświetlenia menu
 *
 * @param {MenuItem[]} menuItems
 * @returns {string[]}
 */
function getRequiredPermissions(menuItems: MenuItem[]): string[] {
  return flat(
    menuItems
      .filter(menuItem => !!menuItem.permissions && menuItem.permissions.length)
      .map(menuItem => menuItemToPermissions(menuItem))
  );
}

/**
 * Wybiera uprawnienia do elementu menu
 *
 * @param {MenuItem} menuItem
 * @returns {string[]}
 */
function menuItemToPermissions(menuItem: MenuItem): string[] {
  if (!menuItem.permissions || !menuItem.permissions.length) {
    return [];
  }
  if (menuItem.entityType && menuItem.entityId) {
    const entityType = menuItem.entityType;
    const entityId = typeof menuItem.entityId === 'function' ? menuItem.entityId() : menuItem.entityId;
    return menuItem.permissions.map(permission => `${permission}:${entityType}:${entityId}`);
  }
  return menuItem.permissions;
}
